import React, { useMemo, useState } from 'react';
import { GoogleMap, Marker, Polyline, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import './TourMap.css';

const MAP_CONTAINER_STYLE = { width: '100%', height: '420px', borderRadius: '12px' };

const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const TourMap = ({ tour, visits }) => {
  const { isLoaded } = useJsApiLoader({
  id: 'haritpane-google-map',
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
});

  const [activeMarker, setActiveMarker] = useState(null);

  // Build ordered stop list: start -> visits (chronological) -> end
  const stops = useMemo(() => {
    const list = [];

    if (tour?.startLatitude && tour?.startLongitude) {
      list.push({
        key: 'start',
        lat: Number(tour.startLatitude),
        lng: Number(tour.startLongitude),
        label: 'S',
        title: 'Tour Start',
        subtitle: `${tour.startDestination || '—'} · ${fmtTime(tour.startTime)} · ${tour.startKm ?? '—'} KM`,
        color: '#2e7d32',
      });
    }

    (visits || []).forEach((v, idx) => {
      list.push({
        key: `visit-${v.id}`,
        lat: v.latitude,
        lng: v.longitude,
        label: String(idx + 1),
        title: v.label || (v.visitType === 'FARMER' ? 'Farmer Visit' : 'Dealer Visit'),
        subtitle: `${v.visitType === 'FARMER' ? (v.visitSubType || 'Farmer Visit') : 'Dealer Visit'}${v.subLabel ? ' · ' + v.subLabel : ''} · ${fmtTime(v.visitTime)}`,
        color: v.visitType === 'FARMER' ? '#1976d2' : '#f57c00',
      });
    });

    if (tour?.endLatitude && tour?.endLongitude) {
      list.push({
        key: 'end',
        lat: Number(tour.endLatitude),
        lng: Number(tour.endLongitude),
        label: 'E',
        title: 'Tour End',
        subtitle: `${fmtTime(tour.endTime)} · ${tour.endKm ?? '—'} KM`,
        color: '#c62828',
      });
    }

    return list;
  }, [tour, visits]);

  const center = stops.length
    ? { lat: stops[0].lat, lng: stops[0].lng }
    : { lat: 20.5937, lng: 78.9629 }; // fallback: India centroid

  const polylinePath = stops.map((s) => ({ lat: s.lat, lng: s.lng }));

  if (!isLoaded) {
    return <div className="tour-map-loading">Loading map...</div>;
  }

  if (stops.length === 0) {
    return (
      <div className="tour-map-empty">
        No location data available for this tour yet.
      </div>
    );
  }

  return (
    <div className="tour-map-wrap">
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={center}
        zoom={12}
        options={{ streetViewControl: false, mapTypeControl: false }}
      >
        <Polyline
          path={polylinePath}
          options={{
            strokeColor: '#1565c0',
            strokeOpacity: 0.8,
            strokeWeight: 3,
            icons: [{
              icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
              offset: '0',
              repeat: '15px',
            }],
          }}
        />

        {stops.map((s) => (
          <Marker
            key={s.key}
            position={{ lat: s.lat, lng: s.lng }}
            label={{ text: s.label, color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: s.color,
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
              scale: 14,
            }}
            onClick={() => setActiveMarker(s.key)}
          >
            {activeMarker === s.key && (
              <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                <div className="tour-map-infowindow">
                  <strong>{s.title}</strong>
                  <p>{s.subtitle}</p>
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}
      </GoogleMap>

      <div className="tour-map-legend">
        <span><i style={{ background: '#2e7d32' }} /> Start</span>
        <span><i style={{ background: '#1976d2' }} /> Farmer Visit</span>
        <span><i style={{ background: '#f57c00' }} /> Dealer Visit</span>
        <span><i style={{ background: '#c62828' }} /> End</span>
      </div>
    </div>
  );
};

export default TourMap;